import React, {useState, useEffect} from "react";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {Card, Col, Row} from "reactstrap";
import {VsiBubble, VsiH4, VsiH6} from "../../../../styled/App.styled";
import {formatBarData, sortChartDataByValue} from "../../../../utils/chartUtils";
import PieChart from "../../../../components/PieChart";
import BarChart from "../../../../components/BarChart";
import Stockchart from "../../../../components/Stockchart";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import { calculateWeightedGrowthRate, forecastWithRateV2, formatStockChartConfig } from "../../../../utils/Helpers";
import { useSearchParams } from "react-router-dom";

const infoAag = [
    {value: 'images', label: 'Images Available'},
    {value: 'pods', label: 'Pods Allocatable'},
    {value: 'running_containers', label: 'Running Containers'}
];

const chartTypesConfig = [
    {value: 'container_cpu_ttl', label: 'CPU'},
    {value: 'container_memory_ttl', label: 'Memory'},
    {value: 'container_storage_ttl', label: 'Disk'}
];

export default function KubernetesClusterOverview({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams] = useSearchParams();

    const [atAGlanceData, setAtAGlanceData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [highCpuBarData, setHighCpuBarData] = useState([]);
    const [highMemoryBarData, setHighMemoryBarData] = useState([]);
    const [trendChart, setTrendChart] = useState([]);
    const [chartConfig, setChartConfig] = useState({});
    const [cpuContainers, setCpuContainers] = useState([]);
    const [memoryContainers, setMemoryContainers] = useState([]);
    const [chartType, setChartType] = useState(chartTypesConfig[0])
    const clusterName = searchParams.get("cluster_name");
    const [data] = useDataWithRoute(`/api/v6/report/kubernetes_cluster_overview_proc?client_id=${client.clientId}&period_id=${periodId}&cluster_name=${encodeURIComponent(clusterName)}`);

    useEffect(() => {
        const validData = data?.recordset || [];
        if(validData.length) {
            calculateOverview(validData || []);
            updateGraphsWithSelector(validData || []);
        }
    }, [data]);
    
    useEffect(() => {
        const validForecastData = data?.recordsets?.[1] || [];
        if(validForecastData.length) {
            forecastResourceUsage(validForecastData || [])
        }
    }, [data, chartType]);

    const forecastResourceUsage = data => {
        let findNodes = {};
        const nodeOrganization = {};

        data?.forEach(datum => {
            if (!findNodes[datum.node]) {
                findNodes[datum.node] = true;
            }

            if (!nodeOrganization[datum.collection_date]) {
                nodeOrganization[datum.collection_date] = {};
            }

            if (!nodeOrganization[datum.collection_date][datum.node]) {
                nodeOrganization[datum.collection_date][datum.node] = {[chartType.value]: 0}
            }

            const axis1DrillDown = nodeOrganization[datum.collection_date][datum.node][chartType.value];
            nodeOrganization[datum.collection_date][datum.node][chartType.value] = axis1DrillDown + datum[chartType.value];
        });

        const sortedNodeKeys = Object.keys(nodeOrganization).sort((a, b) => Date.parse(a?.split?.('T')?.[0]) - Date.parse(b?.split?.('T')?.[0]));
        const allNodes = Object.keys(findNodes);

        const allNodeNamesForChart = [];

        const allNodeData = sortedNodeKeys.map(colDate => {
            const dataToRet = {
                collection_date: colDate,
            };
            const thisDateDataPoint = nodeOrganization[colDate];

            allNodes.forEach(node => {
                const thisDateNodeDataPoint = thisDateDataPoint[node];
                dataToRet[`${node}`] = thisDateNodeDataPoint?.[chartType.value] ?? 0;

                if (!allNodeNamesForChart.includes(`${node}`) && chartType.value !== "none") {
                    allNodeNamesForChart.push(`${node}`);
                }
            });

            return dataToRet;
        });

        const growthRate = calculateWeightedGrowthRate(
            allNodeData,
            allNodeNamesForChart,
            6
        );

        const forecastedData = forecastWithRateV2(
            allNodeData,
            allNodeNamesForChart,
            2,
            growthRate,
            "Months"
        );

        const newData = [...allNodeData, ...forecastedData];

        const allNodeFields = allNodeNamesForChart.map(node => ({
            name: node,
            field: node
        }));

        const allNodesConfig = formatStockChartConfig(newData || [], allNodeFields);

        setChartConfig({
            yAxis: [
                {
                    title: {
                        text: chartType.label,
                    },
                }
            ]
        });

        setTrendChart(allNodesConfig.series)
    };

    const calculateOverview = data => {
        let availableImages = 0;
        let allocatablePods = [];
        let runningContainers = [];
        data.map(datum => {
            if(datum.image) {
                availableImages += 1
            };

            if(!allocatablePods.includes(datum.pod)) {
                allocatablePods.push(datum.pod)
            };

            if(!runningContainers.includes(datum.container) && datum.run) {
                runningContainers.push(datum.container)
            };
        });

        setAtAGlanceData({
            'images': availableImages,
            'pods': allocatablePods.length,
            'running_containers': runningContainers.length
        })
    };

    const updateGraphsWithSelector = data => {
        const pieDict = {};
        const pieData = [];

        let thisData = data || [];

        thisData.forEach(datum => {
            if (!pieDict[datum["node"]]) {
                pieDict[datum["node"]] = 0;
            }
            pieDict[datum["node"]] += datum.cores;
        });

        for (let key in pieDict) {
            pieData.push({
                name: key,
                y: pieDict[key]
            })
        }

        let highCpuBarData = formatBarData(data, {
            sortingArr: ['CPU'],
            sortingField: "container",
            defs: [
                {
                    "field": "container_cpu_ttl",
                    "headerName": "CPU"
                }
            ]
        })

        highCpuBarData = sortChartDataByValue(highCpuBarData)
        highCpuBarData = {
            categories: highCpuBarData.categories.splice(0, 10),
            series: [{
                name: 'CPU',
                data: highCpuBarData.series[0].data.splice(0, 10)
            }]
        }

        let highMemoryBarData = formatBarData(data, {
            sortingArr: ['Memory'],
            sortingField: "container",
            defs: [
                {
                    "field": "container_memory_ttl",
                    "headerName": "Memory"
                }
            ]
        })

        highMemoryBarData = sortChartDataByValue(highMemoryBarData)
        highMemoryBarData = {
            categories: highMemoryBarData.categories.splice(0, 10),
            series: [{
                name: 'Memory',
                data: highMemoryBarData.series[0].data.splice(0, 10)
            }]
        }

        setCpuContainers(highCpuBarData.categories);
        setMemoryContainers(highMemoryBarData.categories);
        setHighCpuBarData(highCpuBarData.series);
        setHighMemoryBarData(highMemoryBarData.series);

        setPieData([
            {
                name: "node",
                colorByPoint: true,
                data: pieData,
            }
        ]);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Cluster Overview - {clusterName || 'N/A'}</VsiH4>
        <Row className={"mb-2"}>
            <Col xs={12} lg={2}>
                <VsiBubble autobotName="AtAGlance" autobotInfo={{data: atAGlanceData, config: infoAag}}>
                    <Row className={`py-2`}>
                        {infoAag.map(config => {
                            return (
                                <div key={`${config.value}-other-detail`}
                                        className={"d-flex flex-row justify-content-between align-items-center mb-1"}>
                                    <VsiH6>
                                        {config.label}
                                    </VsiH6>
                                    <VsiH6>
                                        {atAGlanceData[config.value]}
                                    </VsiH6>
                                </div>
                            )
                        })}
                    </Row>
                </VsiBubble>
            </Col>
            <Col xs={12} lg={4}>
                <VsiBubble>
                    <PieChart
                        height={675}
                        title="Cores Used"
                        series={pieData}
                        includeLegend={true}
                    />
                </VsiBubble>
            </Col>
            <Col xs={12} lg={6}>
                <VsiBubble>
                    <BarChart
                        config={{
                            colors: ["#0C8CFF"]
                        }}
                        yAxisTitle='GHz'
                        title={"Containers with Highest CPU Util"}
                        categories={cpuContainers}
                        height={675}
                        series={highCpuBarData}
                        includeLegend={true}
                        changeAxisTitle={true}
                    />
                </VsiBubble>
            </Col>
        </Row>
        <Row className={"mb-2"}>
            <Col md={12} lg={6}>
            <VsiBubble noAutobot>
              <div className={"d-flex w-100 pb-2"}>
                  <VsiLabeledSelect options={chartTypesConfig}
                                    minWidth={200}
                                    maxWidth={225}
                                    onChange={setChartType}
                                    defaultValue={chartType}/>
              </div>
            </VsiBubble>
                <VsiBubble autobotName="Stockchart">
                    <Stockchart series={trendChart}
                                config={chartConfig}
                                saveParams={{
                                    clusterName,
                                }}
                                title={"Forecasting Node Resource Usage"}
                                height={590}
                                navDisabled={true}
                                rangeSelectorDisabled={true}
                                disableLegend={false} />
                </VsiBubble>
            </Col>
            <Col xs={12} lg={6}>
                <VsiBubble>
                    <BarChart
                        title={"Containers with Highest Memory Util"}
                        categories={memoryContainers}
                        height={675}
                        series={highMemoryBarData}
                        includeLegend={true}
                        changeAxisTitle={true}
                    />
                </VsiBubble>
            </Col>
        </Row>
    </Card>
}
